import { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../config';
import { useAuth } from '../context/AuthContext';

export interface UserMessage {
    id: number | string;
    user_id?: number;
    title: string;
    content: string;
    message_type: string;
    is_read: boolean;
    created_at: string;
    source?: 'personal' | 'broadcast';
}

interface MessagesResponse {
    items: UserMessage[];
    total: number;
    total_personal: number;
}

const DEFAULT_LIMIT = 100;
const LOAD_MORE_STEP = 100;

/**
 * Shared hook for GET /v1/messages/all (personal UserMessage rows + admin broadcasts).
 * `limit` only paginates personal messages server-side (broadcasts always come back in
 * full) — `total` is the true count, so `hasMore` reflects whether more personal messages
 * remain beyond what's currently loaded, and `loadMore()` fetches further.
 * Optional client-side `messageType` filter, e.g. 'BEHAVIORAL_ALERT' for the
 * PortfolioAnalysisScreen alerts section.
 */
export function useUserMessages(messageType?: string) {
    const { token } = useAuth();
    const [allMessages, setAllMessages] = useState<UserMessage[]>([]);
    const [total, setTotal] = useState(0);
    const [limit, setLimit] = useState(DEFAULT_LIMIT);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchMessages = useCallback(async (fetchLimit: number) => {
        if (!token) return;
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_URL}/v1/messages/all?limit=${fetchLimit}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
                const data: MessagesResponse = await response.json();
                setAllMessages(data.items || []);
                setTotal(data.total ?? (data.items || []).length);
            } else {
                setError('Erreur serveur lors de la récupération des messages.');
            }
        } catch (err) {
            console.error('Error fetching user messages:', err);
            setError('Erreur réseau. Vérifiez la connexion API.');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (token) {
            fetchMessages(limit);
        } else {
            setLoading(false);
        }
    }, [token, limit, fetchMessages]);

    const loadMore = useCallback(() => setLimit((l) => l + LOAD_MORE_STEP), []);
    const refetch = useCallback(() => fetchMessages(limit), [fetchMessages, limit]);

    const messages = messageType ? allMessages.filter((m) => m.message_type === messageType) : allMessages;

    return { messages, total, hasMore: allMessages.length < total, loading, error, refetch, loadMore };
}
